import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  Type,
} from '@angular/core';
import { Observable, of, Subscription } from 'rxjs';
import { DocumentSheetService } from '../../services/document-sheet.service';
import { DocumentItemsListQueryEntity, DocumentItemEntity, DocumentTypeAllItems, DocumentItemChangeLogEntity, DocumentFolderChangeLogEntity } from '../../../../meta-data/app-meta-data.service';
import { DwMediaRepositoryService, DwMessage, DwMessageSeverity, DwModalService, DwOrmData } from '@devwareapps/devware-cap';
import { DocumentsRepositoryService } from '../../services/documents-repository.service';
import { DocumentCardData } from '../../models/document-card-data.model';
import { DocumentDataUpdateInfo } from '../../models/docuemnt-data-update-info.model';
import { DocumentItemEditComponent, DocumentSetupResult } from '../../models/document-item-edit-component.interface';

@Component({
  selector: 'app-side-edit-sheet',
  templateUrl: './side-edit-sheet.component.html',
  styleUrls: ['./side-edit-sheet.component.scss'],
})
export class SideEditSheetComponent implements OnInit, OnDestroy {
  // selectedFileForSheet: FileItem | null = null;
  isSheetOpen = false;
  descriptionText = '';
  isDescriptionFocused = false;
  editableTitle = '';
  isDocumentEditActive = false;
  //acknowledgementToggled = false;

  //confirmationVisible = false;
  private originalBodyOverflow: string | null = null;
  private confirmAnimationTimeout: any = null;
  private subscriptions = new Subscription();

  showSavingMessage = false;
  saveComplete = false;
  savingMessage = 'Saving changes...';

  @Input() selectedCardData: DocumentCardData;
  documentCardData: DocumentCardData;

  editorComponentType: Type<any>;
  editor: DocumentItemEditComponent;
  editorSetup: DocumentSetupResult;

  dialogTitle: string = 'Unknown';

  loading = false;

  constructor(
    private renderer: Renderer2,
    protected documentSheetService: DocumentSheetService,
    protected dwMediaRepository: DwMediaRepositoryService,
    protected dwModalService: DwModalService,
    protected documentRespositoryService: DocumentsRepositoryService
  ) { }


  ngOnInit(): void {
    this.setupEvents();
  }

  protected setupEvents(): void {
    try {
      // Subscribe to sheet open/close state
      this.subscriptions.add(
        this.documentSheetService.isSheetOpen$.subscribe(isOpen => {
          try {
            this.isSheetOpen = isOpen;
            if (isOpen && this.isMobileViewport()) {
              this.lockBodyScroll();
            } else if (!isOpen) {
              this.unlockBodyScroll();
            }
          } catch (error) {
            console.error('Error handling sheet open state:', error);
          }
        })
      );

      // Subscribe to selected document changes
      this.subscriptions.add(
        this.documentSheetService.selectedDocument$.subscribe(documentCardData => {
          try {
            this.documentCardData = documentCardData;

            this.loading = true;
            
            switch(documentCardData?.documentCardType) {
              case 'documentItem':
                this.dialogTitle = 'Document';
                this.editableTitle = documentCardData.documentItem?.DocumentItemName || 'Loading...';
                break;
              case 'folder':
                this.dialogTitle = 'Folder';
                this.editableTitle = documentCardData.folderItem?.FolderName || 'Loading...';
                break;
              default:
                this.dialogTitle = 'Unknown';
                break;
            }
            
            this.editorComponentType = this.documentSheetService.getDocumentEditComponent(documentCardData);

            if (this.editor) {
              this.configuredEditorComponent(this.editor);
            }

            //this.initDocumentData(documentCardData);
          } catch (error) {
            console.error('Error mapping document to file item:', error);
          }
        })
      );
    } catch (error) {
      console.error('Error initializing SheetComponent:', error);
    }
  }

  editorSubscriptions: Subscription[] = [];

  configuredEditorComponent(instance: DocumentItemEditComponent) {
    this.clearEditor(false);
    //this.acknowledgementToggled = false; // Reset when new editor component is configured

    this.editor = instance;

    if (!instance) {
      return;
    }

    this.editorSubscriptions.push(this.editor.initDocumentData(this.documentCardData).subscribe((editorSetup) => {
      this.editorSetup = editorSetup;

      if (this.editorSetup.startInEditMode) {
        this.isDocumentEditActive = true;
      }

      this.loading = false;

      this.editableTitle = editorSetup?.initialTitle || '';
    }));

    this.editorSubscriptions.push(this.editor.dataUpdated.subscribe((dataUpdate: DocumentDataUpdateInfo) => {
      this.documentSheetService.notifyDocumentDataUpdated(dataUpdate);
      this.triggerConfirmationAnimation();
    }));

    this.editorSubscriptions.push(this.editor.titleUpdated?.subscribe((newTitle: string) => {
      this.editableTitle = newTitle;
    }));
  }

  private clearEditor(clearEditorComponentType: boolean): void {

    if (clearEditorComponentType) {
      this.editorComponentType = null;

      if (this.editor) {
        this.editor = null;
      }
    }

    if (this.editorSubscriptions.length > 0) {
      this.editorSubscriptions.forEach(sub => sub.unsubscribe());
      this.editorSubscriptions = [];
    }
  }


  ngOnDestroy(): void {
    this.unlockBodyScroll();
    if (this.confirmAnimationTimeout) {
      clearTimeout(this.confirmAnimationTimeout);
      this.confirmAnimationTimeout = null;
    }
    this.subscriptions.unsubscribe();
  }

  closeSheet(): void {

    this.isDocumentEditActive = false;
    //this.acknowledgementToggled = false; // Reset when closing
    this.validationMessage = null;
    this.editorSetup = null;
    

    this.documentSheetService.closeSheet();

    this.clearEditor(true);
    // Don't reset selectedFileForSheet so we can reopen with same data
  }

  deleteFile(): void {
    const fileName = this.editor?.getItemName();

    if (!fileName) return;

    this.dwModalService.showDialog(
      'Confirm Deletion',
      `Are you sure you want to delete "${fileName}"?`)
      .subscribe(modalResult => {
        if (!modalResult.cancelled) {

          this.editor.deleteItemInternal().subscribe((dataInfo) => {
            this.documentSheetService.notifyDocumentDataUpdated(dataInfo);
            this.closeSheet();
          });
        }
      });
  }

  startDocumentEdit(): void {
    this.isDocumentEditActive = true;
  }

  validationMessage: DwMessage;
  confirmChanges(): void {

    if (!this.isFormValid()) {
      return;
    }

    this.showSavingMessage = true;
    this.saveComplete = false;
    this.savingMessage = 'Saving changes...';
 
    this.editor.saveChangesInternal(this.descriptionText)
      .subscribe((dataUpdate) => {
        this.triggerConfirmationAnimation();
        this.documentSheetService.notifyDocumentDataUpdated(dataUpdate);
      }, (error) => {
        console.error('Error saving changes:', error);
      });
  }

  private isFormValid(): boolean {

    this.validationMessage = null;

    const validationResult = this.editor.validateSave();
    if (!validationResult.isValid) {

      this.validationMessage = {
        //messageHeading: 'Please correct the following errors before saving:',
        messageBody: validationResult.message || 'There are validation errors.',
        severity: DwMessageSeverity.error
      };

      return false;
    }

    return true;
  }

  // onDescriptionFocus(): void {
  //   this.isDescriptionFocused = true;
  // }

  private triggerConfirmationAnimation(): void {
    this.showSavingMessage = true;
    this.saveComplete = true;
    this.savingMessage = 'Changes saved';

    if (this.confirmAnimationTimeout) {
      clearTimeout(this.confirmAnimationTimeout);
    } 
    this.confirmAnimationTimeout = setTimeout(() => {
      this.closeSheet();
      this.showSavingMessage = false;

      this.confirmAnimationTimeout = null;
    }, 900);
  }

  // onDescriptionBlur(): void {
  //   this.isDescriptionFocused = false;
  // }

  hasAnyChanges(): boolean {
    // Toggle was interacted with, now check if there are actual changes
    // Check if notes field has content
    if (this.descriptionText && this.descriptionText.trim().length > 0) {
      return true;
    }

    // Check if the editor has any changes
    if (this.editor?.hasChanges()) {
      return true;
    }

    return false;
  }


  // Close sheet on ESC key
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(): void {
    if (this.isSheetOpen) {
      this.closeSheet();
    }
  }

  private isMobileViewport(): boolean {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  private lockBodyScroll(): void {
    const body = document.body;
    if (this.originalBodyOverflow === null) {
      this.originalBodyOverflow = body.style.overflow || '';
    }
    this.renderer.setStyle(body, 'overflow', 'hidden');
    this.renderer.setStyle(body, 'touch-action', 'none');
  }

  private unlockBodyScroll(): void {
    const body = document.body;
    if (this.originalBodyOverflow !== null) {
      this.renderer.setStyle(body, 'overflow', this.originalBodyOverflow);
      this.originalBodyOverflow = null;
    } else {
      this.renderer.removeStyle(body, 'overflow');
    }
    this.renderer.removeStyle(body, 'touch-action');
  }
}
